import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import { MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminCurationTopics = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [formData, setFormData] = useState({
    topic_name: '',
    topic_type: 'google_news',
    search_type: 'query',
    search_query: '',
    hashed_topic: '',
    sort_by: 'relevance',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchTopics();
  }, []);

  // Clear search_query when search_type or topic_type changes to prevent invalid values
  useEffect(() => {
    // Clear search_query when switching between search types
    // to prevent stale Google News topic values from persisting
    setFormData(prev => ({
      ...prev,
      search_query: '',
      hashed_topic: ''
    }));
  }, [formData.search_type, formData.topic_type]);

  const fetchTopics = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/curation-topics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setTopics(response.data.topics || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setError('Failed to load curation topics');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const token = getValidToken();

      // Prepare payload based on search_type
      const payload = {
        topic_name: formData.topic_name,
        topic_type: formData.topic_type,
        search_type: formData.search_type,
        is_active: formData.is_active,
        description: formData.description || null
      };

      // Add appropriate fields based on search_type
      if (formData.search_type === 'query' || formData.search_type === 'topic') {
        payload.search_query = formData.search_query || null;
      } else if (formData.search_type === 'hashed') {
        payload.hashed_topic = formData.hashed_topic || null;
      }

      // Add sort_by for YouTube
      if (formData.topic_type === 'youtube') {
        payload.sort_by = formData.sort_by || 'relevance';
      }

      const endpoint = editingTopic
        ? `${BACKEND_URL}/api/admin/curation-topics/${editingTopic.id}`
        : `${BACKEND_URL}/api/admin/curation-topics`;

      const method = editingTopic ? 'put' : 'post';

      await axios[method](endpoint, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchTopics();
      resetForm();
      alert(editingTopic ? 'Topic updated successfully!' : 'Topic created successfully!');
    } catch (err) {
      console.error('Error saving topic:', err);
      setError(err.response?.data?.detail || 'Failed to save topic');
    }
  };

  const resetForm = () => {
    setFormData({
      topic_name: '',
      topic_type: 'google_news',
      search_type: 'query',
      search_query: '',
      hashed_topic: '',
      sort_by: 'relevance',
      description: '',
      is_active: true
    });
    setEditingTopic(null);
    setShowCreateForm(false);
  };

  const handleEdit = (topic) => {
    setFormData({
      topic_name: topic.topic_name,
      topic_type: topic.topic_type,
      search_type: topic.search_type,
      search_query: topic.search_query || '',
      hashed_topic: topic.hashed_topic || '',
      sort_by: topic.sort_by || 'relevance',
      description: topic.description || '',
      is_active: topic.is_active
    });
    setEditingTopic(topic);
    setShowCreateForm(true);
  };

  const handleDelete = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic? This will clear its cache and remove it from curator views.')) return;

    try {
      const token = getValidToken();
      await axios.delete(`${BACKEND_URL}/api/admin/curation-topics/${topicId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchTopics();
      alert('Topic deleted successfully!');
    } catch (err) {
      console.error('Error deleting topic:', err);
      setError(err.response?.data?.detail || 'Failed to delete topic');
    }
  };

  const handleToggleActive = async (topic) => {
    try {
      const token = getValidToken();
      await axios.put(
        `${BACKEND_URL}/api/admin/curation-topics/${topic.id}`,
        { is_active: !topic.is_active },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      await fetchTopics();
    } catch (err) {
      console.error('Error toggling topic status:', err);
      setError(err.response?.data?.detail || 'Failed to update topic status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.CONTENT_CURATION}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="content-curation"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'profile') {
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            clearPermissionsCache();
            navigate('/');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Curation Topics Management</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Manage news and video topics for content curators
              </p>
            </div>

            {/* Refresh Limits Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800">Curator Refresh Limits</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      <strong>Regular Curators:</strong> 10 manual refreshes per day (Google News and YouTube tracked separately)
                      <br />
                      <strong>Admins:</strong> Unlimited refreshes
                      <br />
                      <span className="text-xs">
                        Note: Topics are cached for 6 hours to reduce API costs. Curators can manually refresh to get fresh content within their daily limit.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Topic
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Back to Admin
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('authToken');
                  clearPermissionsCache();
                  navigate('/');
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="mb-6 sm:mb-8 bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingTopic ? 'Edit Curation Topic' : 'Create New Curation Topic'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Topic Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Topic Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="topic_name"
                    value={formData.topic_name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Technology News, Python Tutorials"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Topic Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Topic Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="topic_type"
                    value={formData.topic_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="google_news">Google News</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>

                {/* Search Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Search Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="search_type"
                    value={formData.search_type}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="query">Query (keyword search)</option>
                    <option value="topic">Topic (category search)</option>
                    <option value="hashed">Hashed Topic (Google News only)</option>
                  </select>
                </div>

                {/* Conditional Fields based on search_type */}
                {formData.search_type === 'query' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Search Query <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="search_query"
                      value={formData.search_query}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., artificial intelligence, climate change"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Advanced Search Filters Collapsible - Only show for query search type */}
              {formData.search_type === 'query' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-blue-100 transition-colors rounded-lg"
                  >
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-semibold text-gray-900">Advanced Search Filters</span>
                    </div>
                    <svg
                      className={`h-5 w-5 text-gray-500 transition-transform ${isAdvancedSearchOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isAdvancedSearchOpen && (
                    <div className="px-4 pb-4 border-t border-blue-200">
                      <div className="pt-4">
                        <p className="text-sm text-gray-700 mb-4">
                          You can use advanced search operators in your queries, such as <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">intitle</code>, <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">inurl</code>, <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">site</code>, exclude operator <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">-</code>, exact match with double-quotes <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">""</code>, <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">AND</code>, <code className="px-1 py-0.5 bg-white rounded text-xs font-mono">OR</code> and more.
                        </p>

                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <h4 className="font-semibold text-gray-900 mb-3 text-sm">Example queries with advanced operators:</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Query</th>
                                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Explained</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                <tr>
                                  <td className="py-2 px-3">
                                    <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap">intitle:"AI" AND site:bbc.com</code>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">Finds articles with "AI" in the title from BBC.</td>
                                </tr>
                                <tr>
                                  <td className="py-2 px-3">
                                    <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap">site:reuters.com "stock market" -crypto</code>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">Finds stock market articles on Reuters, excluding crypto-related ones.</td>
                                </tr>
                                <tr>
                                  <td className="py-2 px-3">
                                    <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap">"Samsung Galaxy S25" AND (review OR comparison)</code>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">Searches for reviews or comparisons of Samsung Galaxy S25.</td>
                                </tr>
                                <tr>
                                  <td className="py-2 px-3">
                                    <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap">site:nytimes.com intitle:"election" after:2025-01-01</code>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">Retrieves recent NY Times articles with "election" keyword in the title.</td>
                                </tr>
                                <tr>
                                  <td className="py-2 px-3">
                                    <code className="text-xs bg-gray-50 px-2 py-1 rounded border border-gray-200 whitespace-nowrap">inurl:blog OR inurl:news "climate change"</code>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">Searches for climate change mentions in blog or news URLs.</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-gray-600 mt-4">
                            For more information, see{' '}
                            <a
                              href="https://www.googleguide.com/advanced_operators_reference.html"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              Google Guide on Search Operators
                            </a>.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formData.search_type === 'topic' && formData.topic_type === 'google_news' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Google News Topic <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="search_query"
                      value={formData.search_query}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a topic...</option>
                      <option value="WORLD">World</option>
                      <option value="NATION">Nation</option>
                      <option value="BUSINESS">Business</option>
                      <option value="TECHNOLOGY">Technology</option>
                      <option value="ENTERTAINMENT">Entertainment</option>
                      <option value="SPORTS">Sports</option>
                      <option value="SCIENCE">Science</option>
                      <option value="HEALTH">Health</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Select from Google News predefined categories
                    </p>
                  </div>
                )}

                {formData.search_type === 'topic' && formData.topic_type === 'youtube' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      YouTube Topic
                    </label>
                    <input
                      type="text"
                      name="search_query"
                      value={formData.search_query}
                      onChange={handleInputChange}
                      placeholder="e.g., programming, cooking"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter a topic or category for YouTube videos
                    </p>
                  </div>
                )}

                {formData.search_type === 'hashed' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Hashed Topic <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="hashed_topic"
                      value={formData.hashed_topic}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtVnVHZ0pWVXlnQVAB"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Google News topic hash (found in Google News URLs)
                    </p>
                  </div>
                )}

                {/* YouTube Sort By */}
                {formData.topic_type === 'youtube' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Sort By
                    </label>
                    <select
                      name="sort_by"
                      value={formData.sort_by}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="relevance">Relevance</option>
                      <option value="date">Date (newest first)</option>
                      <option value="viewCount">View Count</option>
                      <option value="rating">Rating</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Optional description of this topic"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Active (visible to curators)
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingTopic ? 'Update Topic' : 'Create Topic'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Topics List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Curation Topics ({topics.length})</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Topic
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Search
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topics.map((topic) => (
                  <tr key={topic.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{topic.topic_name}</div>
                        {topic.description && (
                          <div className="text-xs text-gray-500">{topic.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        topic.topic_type === 'google_news'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {topic.topic_type === 'google_news' ? 'Google News' : 'YouTube'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium capitalize">{topic.search_type}</div>
                        <div className="text-xs text-gray-500">
                          {topic.search_query || topic.hashed_topic || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(topic)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer ${
                          topic.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {topic.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(topic)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(topic.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {topics.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No curation topics found. Create one to get started.</p>
            </div>
          )}
        </div>
      </div>
    </AdminRouteGuard>
  );
};

export default AdminCurationTopics;
